import { useEffect, useState } from "react";
import { useAuth } from "react-oidc-context";
import { AccessError, GetYtdReport } from "../services/ApiService";
import { downloadCsv } from "../services/CsvService";
import {
  Alert,
  Button,
  Card,
  Col,
  Input,
  Row,
  Select,
  Spin,
  Statistic,
  Table,
  Tabs,
  Tooltip,
  Typography,
} from "antd";
import {
  ApartmentOutlined,
  DollarOutlined,
  DownloadOutlined,
  LoadingOutlined,
  ShopOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip as ReTooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Radio } from "antd";

const { Title, Text } = Typography;
const { Search } = Input;

const COLORS = {
  primary: "#1677ff",
  purple: "#722ed1",
  orange: "#fa8c16",
  cyan: "#13c2c2",
  success: "#52c41a",
};

function fmtNum(n) {
  if (n >= 1e9) return (n / 1e9).toFixed(2) + "B";
  if (n >= 1e6) return (n / 1e6).toFixed(2) + "M";
  if (n >= 1e3) return (n / 1e3).toFixed(1) + "K";
  return n?.toLocaleString() ?? "0";
}

function fmtUsd(n) {
  if (n >= 1e6) return "$" + (n / 1e6).toFixed(2) + "M";
  if (n >= 1e3) return "$" + (n / 1e3).toFixed(1) + "K";
  return "$" + (n ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtPct(n) {
  if (n == null) return "—";
  const sign = n >= 0 ? "+" : "";
  return sign + (n * 100).toFixed(1) + "%";
}

function uniqueOpts(data, field) {
  return [...new Set(data.map((d) => d[field]).filter(Boolean))].sort().map((v) => ({ label: v, value: v }));
}

function isTruthy(v) {
  return v === true || v === "True" || v === "true" || v === "1";
}

// eslint-disable-next-line no-unused-vars -- kept for when the Spend MoM columns below are unhidden
function momTooltip(momPeriod) {
  if (!momPeriod) return "Month-over-month change in solution-attributed spend";
  return `Solution-attributed spend from ${momPeriod.current_start} to ${momPeriod.current_end}, vs. the same date range last month (${momPeriod.previous_start} to ${momPeriod.previous_end})`;
}

function hint(label, tip) {
  return (
    <Tooltip title={tip}>
      <span style={{ borderBottom: "1px dashed #aaa", cursor: "help" }}>{label}</span>
    </Tooltip>
  );
}

// ─── KPI cards ────────────────────────────────────────────────────────────────

function SummaryCards({ summary }) {
  const cards = [
    {
      title: "Total YTD Spend",
      hint: "Spend from Jan 1 to date on entities registered to a custom solution.",
      value: fmtUsd(summary.total_spend_usd), icon: <DollarOutlined />, color: COLORS.purple,
    },
    {
      title: "New Solutions This Year",
      hint: "Solutions launched and registered for impact measurement this year",
      value: summary.new_solutions_this_year ?? "—", icon: <ApartmentOutlined />, color: COLORS.orange,
    },
    {
      title: "YTD Solutions Live",
      hint: "Solutions (new and existing) with measured entities in this year's report",
      value: summary.ytd_solutions_live, icon: <ApartmentOutlined />, color: COLORS.orange,
    },
    {
      title: "YTD Solution Brands",
      hint: "Distinct Product-level brands (for example Kisqali, Baileys) tagged on advertisers that use a solution.",
      value: summary.ytd_brands_live, icon: <ShopOutlined />, color: COLORS.cyan,
    },
    {
      title: "YTD Partner-Attributed Brands",
      hint: "Distinct Parent brands (for example Diageo, Novartis) across all advertisers under solution-owning partners. It is not limited to advertisers that use a solution.",
      value: summary.ytd_top_100_brands_live ?? "—", icon: <ShopOutlined />, color: COLORS.cyan,
    },
    {
      title: "YTD Advertisers",
      hint: "Distinct advertisers with measured entities in this year's report.",
      value: summary.ytd_advertisers_live, icon: <TeamOutlined />, color: COLORS.success,
    },
    {
      title: "YTD Partners",
      hint: "Distinct partners that own those advertisers.",
      value: summary.ytd_partners_live, icon: <TeamOutlined />, color: COLORS.primary,
    },
    {
      title: "YoY Simple Avg Growth",
      hint: "The plain average of each partner's year-over-year spend growth (partner total spend, same date range last year). Partners with under $5,000 of prior-year spend are excluded. Because every partner is weighted equally, partners with a small spend base and high growth can inflate the result.",
      value: fmtPct(summary.yoy_simple_avg), icon: <DollarOutlined />, color: summary.yoy_simple_avg == null ? COLORS.primary : summary.yoy_simple_avg >= 0 ? COLORS.success : "red",
    },
    {
      title: "YoY Weighted Avg Growth",
      hint: "Combined year-over-year growth of the partners' total spend. Large partners dominate it. Partners with no spend last year count their full spend as growth.",
      value: fmtPct(summary.yoy_weighted_avg), icon: <DollarOutlined />, color: summary.yoy_weighted_avg == null ? COLORS.primary : summary.yoy_weighted_avg >= 0 ? COLORS.success : "red",
    },
    {
      title: "YoY Weighted Avg Growth (Like-for-Like)",
      hint: "Same as YoY Weighted Avg Growth, but only counts partners that had spend in the same date range last year. New partners are left out, so their spend doesn't count as growth.",
      value: fmtPct(summary.yoy_weighted_avg_existing), icon: <DollarOutlined />, color: summary.yoy_weighted_avg_existing == null ? COLORS.primary : summary.yoy_weighted_avg_existing >= 0 ? COLORS.success : "red",
    },
  ];
  return (
    <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
      {cards.map((c) => (
        <Col key={c.title} xs={12} sm={8} md={6} lg={4} xl={3}>
          {/* Fill the column and pin the value to the bottom so a long, wrapping title
              doesn't make one card taller or push its value out of line with the others. */}
          <Card bordered={false} style={{ height: "100%", borderTop: `3px solid ${c.color}` }} styles={{ body: { height: "100%" } }}>
            <Statistic
              style={{ height: "100%", display: "flex", flexDirection: "column", justifyContent: "space-between" }}
              title={hint(c.title, c.hint)}
              value={c.value}
              prefix={<span style={{ color: c.color, marginRight: 4 }}>{c.icon}</span>}
            />
          </Card>
        </Col>
      ))}
    </Row>
  );
}

// ─── Top 10 Applications by Spend ─────────────────────────────────────────────

function TruncatedTick({ x, y, payload, maxChars = 32 }) {
  const label = payload.value ?? "";
  const display = label.length > maxChars ? label.slice(0, maxChars) + "…" : label;
  return (
    <text x={x} y={y} dy={4} textAnchor="end" fontSize={11} fill="#555">
      {display}
    </text>
  );
}

function TopApplicationsChart({ data }) {
  const appData = Object.values(
    data.reduce((acc, r) => {
      const k = r.application_id;
      if (!k) return acc;
      if (!acc[k]) acc[k] = { application_id: k, jira_summary: r.jira_summary || k, partner_cost_usd: 0 };
      acc[k].partner_cost_usd = +(acc[k].partner_cost_usd + (r.partner_cost_usd || 0)).toFixed(2);
      return acc;
    }, {})
  )
    .sort((a, b) => b.partner_cost_usd - a.partner_cost_usd)
    .slice(0, 10);

  if (!appData.length) return null;

  return (
    <Card title="Top 10 Applications by Spend" bordered={false} style={{ height: "100%" }}>
      <ResponsiveContainer width="100%" height={Math.max(300, appData.length * 50)}>
        <BarChart data={appData} layout="vertical" margin={{ left: 20, right: 60 }}>
          <CartesianGrid strokeDasharray="3 3" horizontal={false} />
          <XAxis type="number" tickFormatter={fmtUsd} />
          <YAxis type="category" dataKey="jira_summary" width={220} tick={<TruncatedTick />} />
          <ReTooltip
            formatter={(v) => [fmtUsd(v), "Spend"]}
            labelFormatter={(label) => label}
          />
          <Bar dataKey="partner_cost_usd" radius={[0, 4, 4, 0]} barSize={24}>
            {appData.map((_, i) => <Cell key={i} fill={COLORS.orange} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Card>
  );
}

// ─── Spend by Solution Type ───────────────────────────────────────────────────

function SolutionTypeChart({ data }) {
  const typeData = Object.values(
    data.reduce((acc, r) => {
      const k = r.jira_solution_type || "Unknown";
      if (!acc[k]) acc[k] = { jira_solution_type: k, partner_cost_usd: 0, _apps: new Set() };
      acc[k].partner_cost_usd = +(acc[k].partner_cost_usd + (r.partner_cost_usd || 0)).toFixed(2);
      if (r.application_id) acc[k]._apps.add(r.application_id);
      return acc;
    }, {})
  )
    .map((r) => ({ jira_solution_type: r.jira_solution_type, partner_cost_usd: r.partner_cost_usd, solutions: r._apps.size }))
    .sort((a, b) => b.partner_cost_usd - a.partner_cost_usd);

  if (!typeData.length) return null;

  return (
    <Card title="Spend by Solution Type" bordered={false} style={{ height: "100%" }}>
      <ResponsiveContainer width="100%" height={Math.max(260, typeData.length * 40)}>
        <BarChart data={typeData} layout="vertical" margin={{ left: 20, right: 60 }}>
          <CartesianGrid strokeDasharray="3 3" horizontal={false} />
          <XAxis type="number" tickFormatter={fmtUsd} />
          <YAxis type="category" dataKey="jira_solution_type" width={160} tick={{ fontSize: 11 }} />
          <ReTooltip
            formatter={(v, name) =>
              name === "partner_cost_usd" ? [fmtUsd(v), "Spend"] : [v, "# Solutions"]
            }
          />
          <Bar dataKey="partner_cost_usd" radius={[0, 4, 4, 0]}>
            {typeData.map((_, i) => <Cell key={i} fill={COLORS.purple} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Card>
  );
}

// ─── Sector chart ─────────────────────────────────────────────────────────────

const UNKNOWN_SECTOR_LABEL = "Unknown (no sector)";
const isUnknownSector = (r) => !r.sector || r.sector.toLowerCase() === "unknown";

// Keeps the Unknown row last whichever way a column is sorted (antd negates the
// comparator's result for "descend", so the pin has to flip with it).
const unknownLast = (compare) => (a, b, order) => {
  if (a.unknown !== b.unknown) return (a.unknown ? 1 : -1) * (order === "descend" ? -1 : 1);
  return compare(a, b);
};

function SectorChart({ data, momPeriod }) {
  const [yoyType, setYoyType] = useState("weighted");
  if (!data || data.length === 0) return null;

  // Top 10 sectors, then advertisers with no sector in rev_group_metadata as a last
  // row, so their spend is visible rather than dropped.
  const chartData = [
    ...data
      .filter((r) => !isUnknownSector(r))
      .sort((a, b) => b.partner_cost_usd - a.partner_cost_usd)
      .slice(0, 10),
    ...data.filter(isUnknownSector).map((r) => ({ ...r, sector: UNKNOWN_SECTOR_LABEL, unknown: true })),
  ];

  const columns = [
    { title: "Sector", dataIndex: "sector", key: "sector" },
    {
      title: hint("YTD Spend", "Total YTD spend by advertisers linked to a solution in this sector"),
      dataIndex: "partner_cost_usd",
      key: "partner_cost_usd",
      align: "right",
      render: (v) => <Tooltip title={"$" + v?.toLocaleString()}>{fmtUsd(v)}</Tooltip>,
      sorter: unknownLast((a, b) => a.partner_cost_usd - b.partner_cost_usd),
      defaultSortOrder: "descend",
    },
    {
      title: hint("# Solutions", "Number of distinct active solutions (application IDs) tracked in this sector"),
      dataIndex: "solutions",
      key: "solutions",
      align: "right",
      sorter: unknownLast((a, b) => a.solutions - b.solutions),
    },
    // Hidden for now: Spend MoM
    // {
    //   title: hint("Spend MoM", momTooltip(momPeriod)),
    //   dataIndex: "mom_growth",
    //   key: "mom_growth",
    //   align: "right",
    //   sorter: (a, b) => (a.mom_growth ?? -Infinity) - (b.mom_growth ?? -Infinity),
    //   render: (v) => <span style={{ color: v == null ? undefined : v >= 0 ? COLORS.success : "red" }}>{fmtPct(v)}</span>,
    // },
    {
      title: hint("Solution Clients YoY", "Year-over-year spend growth for advertisers who have an active CSOL solution running in this sector"),
      key: "sol_yoy",
      align: "right",
      render: (_, r) => {
        const v = yoyType === "simple" ? r.solution_yoy_simple : r.solution_yoy_weighted;
        return <span style={{ color: v == null ? undefined : v >= 0 ? COLORS.success : "red" }}>{fmtPct(v)}</span>;
      },
    },
    {
      title: hint("All Clients YoY", "Year-over-year spend growth for all advertisers under the same partners as our solution clients (partner-scoped, not all TTD advertisers in the sector)"),
      key: "all_yoy",
      align: "right",
      render: (_, r) => {
        const v = yoyType === "simple" ? r.all_yoy_simple : r.all_yoy_weighted;
        return <span style={{ color: v == null ? undefined : v >= 0 ? COLORS.success : "red" }}>{fmtPct(v)}</span>;
      },
    },
  ];

  return (
    <Card
      title="Spend & # of Solutions per Sector (Top 10, plus Unknown)"
      bordered={false}
      style={{ marginBottom: 24 }}
      extra={
        <Radio.Group value={yoyType} onChange={(e) => setYoyType(e.target.value)} size="small">
          <Radio.Button value="weighted">Weighted YoY</Radio.Button>
          <Radio.Button value="simple">Simple YoY</Radio.Button>
        </Radio.Group>
      }
    >
      <Row gutter={16}>
        <Col xs={24} lg={12}>
          <ResponsiveContainer width="100%" height={Math.max(200, chartData.length * 40)}>
            <BarChart data={chartData} layout="vertical" margin={{ left: 16, right: 60 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" tickFormatter={fmtUsd} />
              <YAxis type="category" dataKey="sector" width={120} tick={{ fontSize: 12 }} />
              <ReTooltip formatter={(v) => [fmtUsd(v), "Spend"]} />
              <Bar dataKey="partner_cost_usd" radius={[0, 4, 4, 0]}>
                {chartData.map((r, i) => <Cell key={i} fill={r.unknown ? "#bfbfbf" : COLORS.cyan} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Col>
        <Col xs={24} lg={12}>
          <Table dataSource={chartData} columns={columns} rowKey="sector" size="small" pagination={false} />
        </Col>
      </Row>
    </Card>
  );
}

// ─── Region chart ─────────────────────────────────────────────────────────────

function RegionChart({ data, momPeriod }) {
  const [yoyType, setYoyType] = useState("weighted");
  if (!data || data.length === 0) return null;

  const columns = [
    { title: "Region", dataIndex: "region", key: "region" },
    {
      title: "YTD Spend (Solution-Attributed)",
      dataIndex: "partner_cost_usd",
      key: "partner_cost_usd",
      align: "right",
      render: (v) => <Tooltip title={"$" + v?.toLocaleString()}>{fmtUsd(v)}</Tooltip>,
      sorter: (a, b) => a.partner_cost_usd - b.partner_cost_usd,
      defaultSortOrder: "descend",
    },
    {
      title: "# Solutions",
      dataIndex: "solutions",
      key: "solutions",
      align: "right",
      sorter: (a, b) => a.solutions - b.solutions,
    },
    // Hidden for now: Spend MoM
    // {
    //   title: (
    //     <Tooltip title={momTooltip(momPeriod)}>
    //       <span style={{ borderBottom: "1px dashed #aaa", cursor: "help" }}>Spend MoM</span>
    //     </Tooltip>
    //   ),
    //   dataIndex: "mom_growth",
    //   key: "mom_growth",
    //   align: "right",
    //   sorter: (a, b) => (a.mom_growth ?? -Infinity) - (b.mom_growth ?? -Infinity),
    //   render: (v) => <span style={{ color: v == null ? undefined : v >= 0 ? COLORS.success : "red" }}>{fmtPct(v)}</span>,
    // },
    {
      title: "Solution Clients YoY",
      key: "yoy",
      align: "right",
      sorter: (a, b) =>
        ((yoyType === "simple" ? a.yoy_simple : a.yoy_weighted) ?? -Infinity) -
        ((yoyType === "simple" ? b.yoy_simple : b.yoy_weighted) ?? -Infinity),
      render: (_, r) => {
        const v = yoyType === "simple" ? r.yoy_simple : r.yoy_weighted;
        return <span style={{ color: v == null ? undefined : v >= 0 ? COLORS.success : "red" }}>{fmtPct(v)}</span>;
      },
    },
  ];

  return (
    <Card
      title="Spend & # of Solutions per Region"
      bordered={false}
      style={{ marginBottom: 24 }}
      extra={
        <Radio.Group value={yoyType} onChange={(e) => setYoyType(e.target.value)} size="small">
          <Radio.Button value="weighted">Weighted YoY</Radio.Button>
          <Radio.Button value="simple">Simple YoY</Radio.Button>
        </Radio.Group>
      }
    >
      <Row gutter={16}>
        <Col xs={24} lg={12}>
          <ResponsiveContainer width="100%" height={Math.max(200, data.length * 40)}>
            <BarChart data={data} layout="vertical" margin={{ left: 16, right: 60 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" tickFormatter={fmtUsd} />
              <YAxis type="category" dataKey="region" width={80} tick={{ fontSize: 12 }} />
              <ReTooltip formatter={(v) => [fmtUsd(v), "Spend"]} />
              <Bar dataKey="partner_cost_usd" radius={[0, 4, 4, 0]}>
                {data.map((_, i) => <Cell key={i} fill={COLORS.primary} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Col>
        <Col xs={24} lg={12}>
          <Table dataSource={data} columns={columns} rowKey="region" size="small" pagination={false} />
        </Col>
      </Row>
    </Card>
  );
}

// ─── Application Performance tab ──────────────────────────────────────────────

function SolutionsTab({ data }) {
  const [search, setSearch] = useState("");
  const [accelerator, setAccelerator] = useState(null);
  const [holdCo, setHoldCo] = useState(null);
  const [region, setRegion] = useState(null);
  const [solutionType, setSolutionType] = useState(null);

  const filtered = data.filter((r) => {
    const q = search.toLowerCase();
    if (q && !r.jira_summary?.toLowerCase().includes(q) && !r.application_id?.toLowerCase().includes(q)) return false;
    if (accelerator === "yes" && !isTruthy(r.accelerator)) return false;
    if (accelerator === "no" && isTruthy(r.accelerator)) return false;
    if (holdCo === "yes" && !isTruthy(r.hold_co_level)) return false;
    if (holdCo === "no" && isTruthy(r.hold_co_level)) return false;
    if (region && r.jira_region !== region) return false;
    if (solutionType && r.jira_solution_type !== solutionType) return false;
    return true;
  });

  const columns = [
    { title: "Application ID", dataIndex: "application_id", key: "application_id", width: 120, render: (v) => <Text code>{v}</Text> },
    { title: "Solution Name", dataIndex: "jira_summary", key: "jira_summary", sorter: (a, b) => a.jira_summary?.localeCompare(b.jira_summary), ellipsis: true, width: 200 },
    { title: "Solution Type", dataIndex: "jira_solution_type", key: "jira_solution_type", width: 130, ellipsis: true },
    { title: "Epic", dataIndex: "jira_epic_name", key: "jira_epic_name", width: 140, ellipsis: true },
    { title: "Region", dataIndex: "jira_region", key: "jira_region", width: 90 },
    { title: "Office", dataIndex: "jira_ttd_office", key: "jira_ttd_office", width: 100 },
    { title: "JBP", dataIndex: "jbp", key: "jbp", width: 65, align: "center" },
    { title: "Accelerator?", dataIndex: "accelerator", key: "accelerator", width: 100, align: "center", render: (v) => isTruthy(v) ? <span style={{ color: COLORS.success }}>Yes</span> : "No" },
    { title: "Hold Co.?", dataIndex: "hold_co_level", key: "hold_co_level", width: 90, align: "center", render: (v) => isTruthy(v) ? <span style={{ color: COLORS.orange }}>Yes</span> : "No" },
    { title: "Partner", dataIndex: "partner_name", key: "partner_name", ellipsis: true, width: 150 },
    { title: "Brand", dataIndex: "brand_name", key: "brand_name", ellipsis: true, width: 140 },
    { title: "Advertiser", dataIndex: "advertiser_name", key: "advertiser_name", ellipsis: true, width: 150 },
    { title: "Impressions", dataIndex: "impressions", key: "impressions", sorter: (a, b) => a.impressions - b.impressions, align: "right", render: (v) => <Tooltip title={v?.toLocaleString()}>{fmtNum(v)}</Tooltip>, width: 120 },
    { title: "YTD Spend", dataIndex: "partner_cost_usd", key: "partner_cost_usd", sorter: (a, b) => a.partner_cost_usd - b.partner_cost_usd, defaultSortOrder: "descend", align: "right", render: (v) => <Tooltip title={"$" + v?.toLocaleString()}>{fmtUsd(v)}</Tooltip>, width: 130 },
  ];

  return (
    <Card bordered={false}>
      <Row gutter={[8, 8]} style={{ marginBottom: 12 }} justify="space-between" align="middle">
        <Col>
          <Row gutter={[8, 8]}>
            <Col>
              <Search placeholder="Search solution…" onChange={(e) => setSearch(e.target.value)} style={{ width: 220 }} allowClear />
            </Col>
            <Col>
              <Select
                placeholder="Solution Type"
                options={uniqueOpts(data, "jira_solution_type")}
                onChange={setSolutionType}
                allowClear
                style={{ width: 160 }}
              />
            </Col>
            <Col>
              <Select
                placeholder="Region"
                options={uniqueOpts(data, "jira_region")}
                onChange={setRegion}
                allowClear
                style={{ width: 130 }}
              />
            </Col>
            <Col>
              <Select
                placeholder="Accelerator?"
                options={[{ label: "Yes", value: "yes" }, { label: "No", value: "no" }]}
                onChange={setAccelerator}
                allowClear
                style={{ width: 130 }}
              />
            </Col>
            <Col>
              <Select
                placeholder="Hold Co. Project?"
                options={[{ label: "Yes", value: "yes" }, { label: "No", value: "no" }]}
                onChange={setHoldCo}
                allowClear
                style={{ width: 150 }}
              />
            </Col>
          </Row>
        </Col>
        <Col>
          <Button
            icon={<DownloadOutlined />}
            onClick={() => downloadCsv(filtered, "application_performance.csv")}
          >
            Export CSV
          </Button>
        </Col>
      </Row>
      <Table
        dataSource={filtered}
        columns={columns}
        rowKey={(r) => `${r.application_id}-${r.advertiser_id}`}
        size="small"
        scroll={{ x: 1700 }}
        pagination={{ pageSize: 20 }}
      />
    </Card>
  );
}

// ─── Partner tab ──────────────────────────────────────────────────────────────

function PartnerTab({ data }) {
  const [search, setSearch] = useState("");
  const q = search.toLowerCase();
  const filtered = data.filter((r) =>
    !q || r.partner_name?.toLowerCase().includes(q) || r.partner_id?.toLowerCase().includes(q)
  );
  const columns = [
    { title: "Partner ID", dataIndex: "partner_id", key: "partner_id", width: 110, render: (v) => <Text code>{v}</Text> },
    { title: "Partner Name", dataIndex: "partner_name", key: "partner_name", sorter: (a, b) => a.partner_name?.localeCompare(b.partner_name), ellipsis: true },
    { title: "YTD Spend", dataIndex: "partner_cost_usd", key: "partner_cost_usd", sorter: (a, b) => a.partner_cost_usd - b.partner_cost_usd, defaultSortOrder: "descend", align: "right", render: (v) => <Tooltip title={"$" + v?.toLocaleString()}>{fmtUsd(v)}</Tooltip>, width: 130 },
    { title: "Prior YTD Spend", dataIndex: "prior_ytd_spend", key: "prior_ytd_spend", sorter: (a, b) => a.prior_ytd_spend - b.prior_ytd_spend, align: "right", render: (v) => <Tooltip title={"$" + v?.toLocaleString()}>{fmtUsd(v)}</Tooltip>, width: 140 },
    { title: "YoY Growth", dataIndex: "yoy_growth", key: "yoy_growth", sorter: (a, b) => (a.yoy_growth ?? -Infinity) - (b.yoy_growth ?? -Infinity), align: "right", render: (v) => <span style={{ color: v == null ? undefined : v >= 0 ? COLORS.success : "red" }}>{fmtPct(v)}</span>, width: 110 },
  ];
  const csvRows = filtered.map((r) => ({
    "Partner ID": r.partner_id,
    "Partner Name": r.partner_name,
    "YTD Spend": r.partner_cost_usd,
    "Prior YTD Spend": r.prior_ytd_spend,
    "YoY Growth": r.yoy_growth == null ? "" : fmtPct(r.yoy_growth),
  }));
  return (
    <Card bordered={false}>
      <Row gutter={[8, 8]} style={{ marginBottom: 12 }} justify="space-between" align="middle">
        <Col>
          <Search placeholder="Search partner…" onChange={(e) => setSearch(e.target.value)} style={{ width: 280 }} allowClear />
        </Col>
        <Col>
          <Button
            icon={<DownloadOutlined />}
            onClick={() => downloadCsv(csvRows, "partner_ytd.csv")}
          >
            Export CSV
          </Button>
        </Col>
      </Row>
      <Table dataSource={filtered} columns={columns} rowKey="partner_id" size="small" pagination={{ pageSize: 20 }} />
    </Card>
  );
}

// ─── Brand tab ────────────────────────────────────────────────────────────────

function BrandTab({ data }) {
  const [search, setSearch] = useState("");
  const q = search.toLowerCase();
  const filtered = data.filter((r) =>
    !q || r.brand_name?.toLowerCase().includes(q) || r.brand_id?.toLowerCase().includes(q)
  );
  const columns = [
    { title: "Brand ID", dataIndex: "brand_id", key: "brand_id", width: 90, render: (v) => <Text code>{v}</Text> },
    { title: "Brand Name", dataIndex: "brand_name", key: "brand_name", sorter: (a, b) => a.brand_name?.localeCompare(b.brand_name), ellipsis: true },
    { title: "YTD Spend", dataIndex: "partner_cost_usd", key: "partner_cost_usd", sorter: (a, b) => a.partner_cost_usd - b.partner_cost_usd, defaultSortOrder: "descend", align: "right", render: (v) => <Tooltip title={"$" + v?.toLocaleString()}>{fmtUsd(v)}</Tooltip>, width: 130 },
    { title: "Prior YTD Spend", dataIndex: "prior_ytd_spend", key: "prior_ytd_spend", sorter: (a, b) => a.prior_ytd_spend - b.prior_ytd_spend, align: "right", render: (v) => <Tooltip title={"$" + v?.toLocaleString()}>{fmtUsd(v)}</Tooltip>, width: 140 },
    { title: "YoY Growth", dataIndex: "yoy_growth", key: "yoy_growth", sorter: (a, b) => (a.yoy_growth ?? -Infinity) - (b.yoy_growth ?? -Infinity), align: "right", render: (v) => <span style={{ color: v == null ? undefined : v >= 0 ? COLORS.success : "red" }}>{fmtPct(v)}</span>, width: 110 },
  ];
  const csvRows = filtered.map((r) => ({
    "Brand ID": r.brand_id,
    "Brand Name": r.brand_name,
    "YTD Spend": r.partner_cost_usd,
    "Prior YTD Spend": r.prior_ytd_spend,
    "YoY Growth": r.yoy_growth == null ? "" : fmtPct(r.yoy_growth),
  }));
  return (
    <Card bordered={false}>
      <Row gutter={[8, 8]} style={{ marginBottom: 12 }} justify="space-between" align="middle">
        <Col>
          <Search placeholder="Search brand…" onChange={(e) => setSearch(e.target.value)} style={{ width: 280 }} allowClear />
        </Col>
        <Col>
          <Button
            icon={<DownloadOutlined />}
            onClick={() => downloadCsv(csvRows, "brand_ytd.csv")}
          >
            Export CSV
          </Button>
        </Col>
      </Row>
      <Table dataSource={filtered} columns={columns} rowKey="brand_id" size="small" pagination={{ pageSize: 20 }} />
    </Card>
  );
}

// ─── Advertiser tab ───────────────────────────────────────────────────────────

function AdvertiserTab({ data }) {
  const [search, setSearch] = useState("");
  const [office, setOffice] = useState(null);
  const [holdco, setHoldco] = useState(null);
  const [vertical, setVertical] = useState(null);
  const [businessUnit, setBusinessUnit] = useState(null);
  const [sector, setSector] = useState(null);
  const [portfolio, setPortfolio] = useState(null);

  const filtered = data.filter((r) => {
    const q = search.toLowerCase();
    if (q && !r.advertiser_name?.toLowerCase().includes(q) && !r.advertiser_id?.toLowerCase().includes(q)) return false;
    if (office && r.office_name !== office) return false;
    if (holdco && r.holding_company_name !== holdco) return false;
    if (vertical && r.vertical_name !== vertical) return false;
    if (businessUnit && r.business_unit !== businessUnit) return false;
    if (sector && r.sector !== sector) return false;
    if (portfolio && r.portfolio !== portfolio) return false;
    return true;
  });

  const columns = [
    { title: "Office", dataIndex: "office_name", key: "office_name", width: 110, filters: uniqueOpts(data, "office_name").map((o) => ({ text: o.label, value: o.value })), onFilter: (v, r) => r.office_name === v },
    { title: "Business Unit", dataIndex: "business_unit", key: "business_unit", width: 140, ellipsis: true, filters: uniqueOpts(data, "business_unit").map((o) => ({ text: o.label, value: o.value })), onFilter: (v, r) => r.business_unit === v },
    { title: "Sector", dataIndex: "sector", key: "sector", width: 130, ellipsis: true, filters: uniqueOpts(data, "sector").map((o) => ({ text: o.label, value: o.value })), onFilter: (v, r) => r.sector === v },
    { title: "Portfolio", dataIndex: "portfolio", key: "portfolio", width: 130, ellipsis: true, filters: uniqueOpts(data, "portfolio").map((o) => ({ text: o.label, value: o.value })), onFilter: (v, r) => r.portfolio === v },
    { title: "Holding Company", dataIndex: "holding_company_name", key: "holding_company_name", ellipsis: true, width: 160 },
    { title: "Partner", dataIndex: "partner_name", key: "partner_name", ellipsis: true, width: 160 },
    { title: "Brand", dataIndex: "brand_name", key: "brand_name", ellipsis: true, width: 140 },
    { title: "Advertiser ID", dataIndex: "advertiser_id", key: "advertiser_id", width: 110, render: (v) => <Text code>{v}</Text> },
    { title: "Advertiser Name", dataIndex: "advertiser_name", key: "advertiser_name", sorter: (a, b) => a.advertiser_name?.localeCompare(b.advertiser_name), ellipsis: true },
    { title: "Vertical", dataIndex: "vertical_name", key: "vertical_name", width: 130, filters: uniqueOpts(data, "vertical_name").map((o) => ({ text: o.label, value: o.value })), onFilter: (v, r) => r.vertical_name === v },
    { title: "YTD Spend", dataIndex: "partner_cost_usd", key: "partner_cost_usd", sorter: (a, b) => a.partner_cost_usd - b.partner_cost_usd, defaultSortOrder: "descend", align: "right", render: (v) => <Tooltip title={"$" + v?.toLocaleString()}>{fmtUsd(v)}</Tooltip>, width: 130 },
    { title: "Prior YTD Spend", dataIndex: "prior_ytd_spend", key: "prior_ytd_spend", sorter: (a, b) => a.prior_ytd_spend - b.prior_ytd_spend, align: "right", render: (v) => <Tooltip title={"$" + v?.toLocaleString()}>{fmtUsd(v)}</Tooltip>, width: 140 },
    { title: "YoY Growth", dataIndex: "yoy_growth", key: "yoy_growth", sorter: (a, b) => (a.yoy_growth ?? -Infinity) - (b.yoy_growth ?? -Infinity), align: "right", render: (v) => <span style={{ color: v == null ? undefined : v >= 0 ? COLORS.success : "red" }}>{fmtPct(v)}</span>, width: 110 },
  ];

  const csvRows = filtered.map((r) => ({
    Office: r.office_name,
    "Business Unit": r.business_unit,
    Sector: r.sector,
    Portfolio: r.portfolio,
    "Holding Company": r.holding_company_name,
    Partner: r.partner_name,
    Brand: r.brand_name,
    "Advertiser ID": r.advertiser_id,
    "Advertiser Name": r.advertiser_name,
    Vertical: r.vertical_name,
    "YTD Spend": r.partner_cost_usd,
    "Prior YTD Spend": r.prior_ytd_spend,
    "YoY Growth": r.yoy_growth == null ? "" : fmtPct(r.yoy_growth),
  }));

  return (
    <Card bordered={false}>
      <Row gutter={[8, 8]} style={{ marginBottom: 12 }} justify="space-between" align="middle">
        <Col>
          <Row gutter={[8, 8]}>
            <Col><Search placeholder="Search advertiser…" onChange={(e) => setSearch(e.target.value)} style={{ width: 220 }} allowClear /></Col>
            <Col><Select placeholder="Office" options={uniqueOpts(data, "office_name")} onChange={setOffice} allowClear style={{ width: 150 }} /></Col>
            <Col><Select placeholder="Business Unit" options={uniqueOpts(data, "business_unit")} onChange={setBusinessUnit} allowClear style={{ width: 160 }} /></Col>
            <Col><Select placeholder="Sector" options={uniqueOpts(data, "sector")} onChange={setSector} allowClear style={{ width: 150 }} /></Col>
            <Col><Select placeholder="Portfolio" options={uniqueOpts(data, "portfolio")} onChange={setPortfolio} allowClear style={{ width: 150 }} /></Col>
            <Col><Select placeholder="Holding Company" options={uniqueOpts(data, "holding_company_name")} onChange={setHoldco} allowClear style={{ width: 180 }} /></Col>
            <Col><Select placeholder="Vertical" options={uniqueOpts(data, "vertical_name")} onChange={setVertical} allowClear style={{ width: 150 }} /></Col>
          </Row>
        </Col>
        <Col>
          <Button
            icon={<DownloadOutlined />}
            onClick={() => downloadCsv(csvRows, "advertiser_ytd.csv")}
          >
            Export CSV
          </Button>
        </Col>
      </Row>
      <Table
        dataSource={filtered}
        columns={columns}
        rowKey={(r) => r.advertiser_id || r.advertiser_name}
        size="small"
        scroll={{ x: 1900 }}
        pagination={{ pageSize: 20 }}
      />
    </Card>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function YtdReport() {
  const auth = useAuth();
  const token = auth.user?.id_token;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!token) return;
    GetYtdReport(token)
      .then(setData)
      .catch((e) => setError(e instanceof AccessError ? e.message : "Failed to load YTD report."))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) return (
    <div style={{ padding: 24 }}>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", marginTop: 80, gap: 16 }}>
        <Spin indicator={<LoadingOutlined style={{ fontSize: 40 }} spin />} />
        <Text type="secondary">Loading YTD report…</Text>
      </div>
    </div>
  );
  if (error) return <Alert type="error" message={error} style={{ margin: 24 }} />;
  if (!data) return null;

  const tabs = [
    { key: "advertiser", label: "Advertiser YTD", children: <AdvertiserTab data={data.by_advertiser} /> },
    { key: "brand", label: "Brand YTD", children: <BrandTab data={data.by_brand} /> },
    { key: "partner", label: "Partner YTD", children: <PartnerTab data={data.by_partner} /> },
    { key: "solutions", label: "Application YTD", children: <SolutionsTab data={data.by_solution || []} /> },
  ];

  return (
    <div style={{ padding: "24px 24px 0" }}>
      <Title level={4} style={{ marginBottom: 20 }}>Custom Solutions - YTD Commercial Impact Measurement Report</Title>
      <SummaryCards summary={data.summary} />
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} lg={12}>
          <TopApplicationsChart data={data.by_solution || []} />
        </Col>
        <Col xs={24} lg={12}>
          <SolutionTypeChart data={data.by_solution || []} />
        </Col>
      </Row>
      <SectorChart data={data.by_sector || []} momPeriod={data.mom_period} />
      <RegionChart data={data.by_region || []} momPeriod={data.mom_period} />
      <Tabs defaultActiveKey="advertiser" items={tabs} />
    </div>
  );
}
